import React, { useState, useEffect } from 'react';
import {
    Violator,
    GalleryNext,
    GalleryNextSlides,
    GalleryNextThumbnailsUS,
    GalleryNextBadges,
    GalleryNextExpandBtn,
    useGalleryNextContext,
    ImageWithPlaceholder,
} from '@hpi-sox/hpstellar-core';
import { Gallery } from '@hpi-sox/hpstellar-core/legacy';

import loadable from '@loadable/component';
import { Helpers } from '../../../../core/src/helpers';

import withConfigurations from '../../../../product/components/product-configurations';
import useTemplateTheme from '../../../../hooks/useTemplateTheme';
import {
    getEnvironmentalCertificationBadges,
    getConfigurations,
    getSelectedVariant,
    getColorId,
} from '../../../../product/get-product-props';
import withPageComponents from '../../../../page/components/with-page-components';
import LCPPrefetch, { getPrefetchImageArray } from '../../../../shared/components/lcp-prefetch';
import ImageModal from './image-modal';
import AwardBadges from './badges-with-award';

import './pdp-gallery.less';

const GalleryEnhanced = loadable(() => Helpers.retryFunc(() => import('./gallery-enhanced')));

const mapImages = (images, selectedVariant) => {
    let { fullImages, zoomImages: xlImages, zoomedFullImages } = images;
    let defaultImages = fullImages || [];
    let zoomImages = xlImages || [];
    if (selectedVariant) {
        const { value } = selectedVariant;
        //TODO: If more than one color and there is no match, might need to default to the first color seen
        const filterByColor = ({ color }) => !color || getColorId(color) === getColorId(value);
        defaultImages = (fullImages || []).filter(filterByColor);
        zoomImages = (xlImages || []).filter(filterByColor);
        zoomedFullImages = (zoomedFullImages || []).filter(filterByColor);
    }

    return {
        defaultImages,
        zoomImages,
        zoomedFullImages,
    };
};

/**
 * Replaces video thumbnail with first product images.
 * Returns only the first image for SSR, since lazy load doesn't work well with SSR
 * @param {*} images
 * @param {*} mounted
 * @returns
 */
const setVideoDefaultImages = (images, mounted, productInitial) => {
    if (!images || images.length === 0) return [];

    const alt = `${productInitial.pm_category || productInitial.pm_subcategory}, ${productInitial.name}`;
    const setImageAlt = (img, count) => {
        return { ...img, alt: `Series ${count} of ${images.length} ${alt}` };
    };
    //find first asset that is not a video and use it as the thumbnail
    const imageIndex = images.findIndex(t => t.type !== 'video');
    const firstImage = { ...(images[imageIndex] || {}), alt };
    if (!mounted) {
        return [firstImage, ...images.slice(1).map((i, idx) => ({ url: '' }))];
    }
    return images.map((img, index) => {
        if (img.type === 'video') {
            return { ...img, url: firstImage.url, alt };
        }
        return setImageAlt(img, index + 1);
    });
};

/**
 * Adds environmental certification badges to to any existing partner logos
 * @param {*} product
 * @param {*} pdpPartnerLogos
 * @returns
 */
const getBadges = (product, pdpPartnerLogos) => {
    const envBadges = getEnvironmentalCertificationBadges(product);
    const { logos, awards } = pdpPartnerLogos || [];
    return envBadges.length > 0 ? { logos: [...(logos || []), ...envBadges], awards } : pdpPartnerLogos || [];
};

const pdpGallery = ({ pdpImages, ctoConfig, pdpPartnerLogos, productInitial, ctaProps, enableZoomGallery = true }) => {
    if (pdpImages && productInitial) {
        const { productImage } = useTemplateTheme(['productImage']);
        const { classes } = productImage || {};
        const { colorCTOs, sku, facet_aipc, dte_facet_OS } = productInitial;
        const { openGalleryGtmActions, getGtmActions, has3D, hasVideo } = pdpImages || {};
        const [imageIndex, setImageIndex] = useState(-1);
        const [mounted, setMounted] = useState(false);
        //don't render gallery till interaction is made
        const [loadModal, setLoadModal] = useState(false);
        const configurations = getConfigurations(colorCTOs, ctoConfig.color);
        const selectedVariant = getSelectedVariant(configurations, ctoConfig.color);
        const mappedImages = mapImages(pdpImages, selectedVariant);
        const images = setVideoDefaultImages(mappedImages?.defaultImages, mounted, productInitial);
        const { logos: badges, awards } = getBadges(productInitial, pdpPartnerLogos);

        const prefetchList = (() => {
            if (!images || images.length === 0) return [];
            const list = [];
            const first = images[0];
            if (first) list.push(first);
            const img360 = images.find(i => i && (i.type === '360' || i.type === 360));
            if (img360 && img360.url && img360.url !== first?.url) {
                list.push(img360);
            }
            return list;
        })();

        const prefetchImages = getPrefetchImageArray(`${sku}-${prefetchList.length}`, prefetchList, image => ({
            href: image?.url,
            type: image.type === '360' ? 'fetch' : 'image',
        }));

        useEffect(() => {
            if (!mounted) {
                setMounted(!mounted);
            }
        });
        if (!images || images.length < 1) {
            return null;
        }

        const onExpand = index => {
            if (enableZoomGallery) {
                try {
                    if (!loadModal) {
                        setLoadModal(true);
                    }
                    setImageIndex(index);
                } catch (e) {}
            }
        };

        //TODO: Temp solution would be better to have Gallery component take lazyLoad as a prop
        return (
            <>
                {awards && <AwardBadges className="award-logos" badges={awards} />}
                <LCPPrefetch images={prefetchImages} />

                {has3D ? (
                    <GalleryEnhanced images={images} badges={badges} onExpand={onExpand} hasVideo={hasVideo} />
                ) : (
                    <Gallery
                        /** set key to cto color so switch color options with different amount of images doesn't break*/
                        key={ctoConfig.color || 'default'}
                        onExpand={index => onExpand(index)}
                        className={`pdp-gallery${!mounted ? ' ssr' : ''}${awards?.length > 0 ? ' badges-awards' : ''}`}
                        images={mounted ? images : images.slice(0, 1)}
                        lazyLoad={mounted ? 'ondemand' : null}
                        gtmActions={openGalleryGtmActions}
                        badges={badges}
                        badgesPosition="bottom-center"
                        controls="thumbnails"
                        thumbnailsOrientation="vertical"
                        badgesCustomSize
                        classes={{
                            ...(classes || {}),
                            badge: (classes && classes.badge ? classes.badge + ' ' : '') + 'gallery-badges',
                        }}
                    />
                )}
                {!has3D && badges && badges.length > 0 && (
                    <div className="badge-divider" aria-hidden="true" role="presentation" />
                )}
                {loadModal && (
                    <ImageModal
                        imageIndex={imageIndex}
                        setImageIndex={setImageIndex}
                        images={mappedImages}
                        ctoConfig={ctoConfig}
                        getGtmActions={getGtmActions}
                        ctaProps={ctaProps}
                        has3D={has3D}
                    />
                )}
            </>
        );
    }
    return null;
};

export default withPageComponents(withConfigurations(pdpGallery), {
    components: ['pdpImages', 'productInitial', 'pdpPartnerLogos'],
});
